import React, { useState } from 'react';
import { Card, Button, Collapse, Badge, Alert } from 'react-bootstrap';
import { Building2, ChevronDown, ChevronUp, Users, Briefcase, Clock, AlertTriangle, Edit2, UserCheck } from 'lucide-react';
import moment from 'moment';
import { toast } from 'sonner';
import UpdateAssignmentModal from './UpdateAssignmentModal';

const AssignmentCard = ({ employeeId, companyId, currentAssignment, history, currentContract, onUpdate }) => {
  const [showHistory, setShowHistory] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'

  const handleCreateClick = () => {
    if (!currentContract) {
      toast.error('Ett anställningskontrakt måste finnas innan tjänst kan läggas till');
      return;
    }
    setModalMode('create');
    setShowUpdateModal(true);
  };

  const handleEditClick = () => {
    setModalMode('edit');
    setShowUpdateModal(true);
  };

  const formatDate = (date) => {
    return moment(date).format('DD MMM YYYY');
  };

  const handleUpdateSuccess = () => {
    setShowUpdateModal(false);
    if (onUpdate) onUpdate();
  };

  if (!currentAssignment) {
    // Empty state must still mount UpdateAssignmentModal — otherwise the
    // "Lägg till placering nu" button toggles showUpdateModal on a modal
    // that isn't in the tree, and clicking it does nothing.
    return (
      <>
        <div className="info-section mb-4" role="region" aria-labelledby="assignment-heading">
          <div className="p-4">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <Building2 size={20} className="me-3 text-primary" aria-hidden="true" />
                <div>
                  <h5 className="mb-0 fw-bold" id="assignment-heading">Aktuell placering</h5>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.875rem' }}>
                    Ingen placering registrerad
                  </p>
                </div>
              </div>
            </div>
            <div className="section-divider"></div>
            <div className="text-center py-5">
              <div className="mb-4">
                <div className="d-inline-flex align-items-center justify-content-center rounded-circle bg-light" style={{ width: '80px', height: '80px' }}>
                  <Building2 size={40} className="text-primary" aria-hidden="true" />
                </div>
              </div>
              <h5 className="mb-2">Ingen placering har registrerats än</h5>
              <p className="text-muted mb-4">
                Lägg till position och avdelning för att organisera anställda i företaget.
              </p>
              <Button
                variant="primary"
                size="lg"
                className="action-button d-flex align-items-center mx-auto"
                onClick={handleCreateClick}
                aria-label="Lägg till placering för anställd"
              >
                <Briefcase size={18} className="me-2" aria-hidden="true" />
                Lägg till placering nu
              </Button>
            </div>
          </div>
        </div>

        <UpdateAssignmentModal
          show={showUpdateModal}
          handleClose={() => setShowUpdateModal(false)}
          employeeId={employeeId}
          companyId={companyId}
          currentAssignment={null}
          mode="create"
          onSuccess={handleUpdateSuccess}
        />
      </>
    );
  }

  return (
    <>
      <div className="info-section mb-4" role="region" aria-labelledby="assignment-heading">
        <div className="p-4">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <Building2 size={20} className="me-3 text-primary" aria-hidden="true" />
              <div>
                <h5 className="mb-0 fw-bold" id="assignment-heading">Aktuell placering</h5>
                <p className="mb-0 text-muted" style={{ fontSize: '0.875rem' }}>
                  Gäller från {formatDate(currentAssignment.valid_from)}
                </p>
              </div>
            </div>
            <Button
              variant="primary"
              className="action-button d-flex align-items-center"
              onClick={handleCreateClick}
              aria-label="Skapa ny placering för anställd"
            >
              <Briefcase size={16} className="me-2" aria-hidden="true" />
              Skapa ny placering
            </Button>
          </div>
          <div className="section-divider"></div>
          {/* Current Assignment Display */}
          <div className="row g-3 mb-3">
            <div className="col-md-4">
              <div className="p-3 border rounded-lg position-relative">
                <Button
                  variant="link"
                  size="sm"
                  className="position-absolute top-0 end-0 mt-2 me-2 p-1 text-primary"
                  onClick={handleEditClick}
                  aria-label="Redigera placering"
                  style={{ minWidth: 'auto' }}
                >
                  <Edit2 size={16} />
                </Button>
                <div className="d-flex align-items-center mb-2">
                  <Briefcase size={18} className="me-2 text-primary" />
                  <div className="text-muted small">Position</div>
                </div>
                <div className="h5 mb-0 fw-semibold">
                  {currentAssignment.position || 'Ej angiven'}
                </div>
                {currentAssignment.position_level && (
                  <Badge bg="info" className="mt-2">
                    {currentAssignment.position_level}
                  </Badge>
                )}
              </div>
            </div>
            <div className="col-md-4">
              <div className="p-3 border rounded-lg position-relative">
                <Button
                  variant="link"
                  size="sm"
                  className="position-absolute top-0 end-0 mt-2 me-2 p-1 text-primary"
                  onClick={handleEditClick}
                  aria-label="Redigera placering"
                  style={{ minWidth: 'auto' }}
                >
                  <Edit2 size={16} />
                </Button>
                <div className="d-flex align-items-center mb-2">
                  <Users size={18} className="me-2 text-primary" />
                  <div className="text-muted small">Avdelning</div>
                </div>
                <div className="h5 mb-0 fw-semibold">
                  {currentAssignment.department || 'Ej angiven'}
                </div>
                {currentAssignment.cost_center && (
                  <div className="text-muted small mt-2">
                    Kostnadsställe: {currentAssignment.cost_center}
                  </div>
                )}
              </div>
            </div>
            <div className="col-md-4">
              <div className="p-3 border rounded-lg position-relative">
                <Button
                  variant="link"
                  size="sm"
                  className="position-absolute top-0 end-0 mt-2 me-2 p-1 text-primary"
                  onClick={handleEditClick}
                  aria-label="Redigera chef"
                  style={{ minWidth: 'auto' }}
                >
                  <Edit2 size={16} />
                </Button>
                <div className="d-flex align-items-center mb-2">
                  <UserCheck size={18} className="me-2 text-primary" />
                  <div className="text-muted small">Chef</div>
                </div>
                <div className="h5 mb-0 fw-semibold">
                  {(currentAssignment.manager_firstname || currentAssignment.manager_lastname)
                    ? [currentAssignment.manager_firstname, currentAssignment.manager_lastname].filter(Boolean).join(' ')
                    : 'Ingen chef'}
                </div>
                {!currentAssignment.manager_employee_id && (
                  <div className="text-muted small mt-2">
                    Tidrapporter godkänns automatiskt
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Additional Info */}
          {currentAssignment.grade && (
            <div className="row g-2 mb-3">
              <div className="col-auto">
                <Badge bg="secondary" className="py-2 px-3">
                  Grad: {currentAssignment.grade}
                </Badge>
              </div>
            </div>
          )}

          {/* History Toggle */}
          {history && history.length > 1 && (
            <div className="mt-3">
              <Button
                variant="link"
                size="sm"
                onClick={() => setShowHistory(!showHistory)}
                className="d-flex align-items-center p-0 text-decoration-none"
              >
                {showHistory ? <ChevronUp size={16} className="me-1" /> : <ChevronDown size={16} className="me-1" />}
                {showHistory ? 'Dölj' : 'Visa'} placeringshistorik ({history.length - 1} tidigare ändringar)
              </Button>

              <Collapse in={showHistory}>
                <div className="mt-3">
                  <table className="table table-sm table-hover">
                    <thead>
                      <tr>
                        <th>Period</th>
                        <th>Position</th>
                        <th>Avdelning</th>
                        <th>Chef</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((assignment) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        
                        const validFrom = new Date(assignment.valid_from);
                        validFrom.setHours(0, 0, 0, 0);
                        
                        const validTo = assignment.valid_to ? new Date(assignment.valid_to) : null;
                        if (validTo) {
                          validTo.setHours(0, 0, 0, 0);
                        }
                        
                        // Determine status based on date ranges
                        let status = 'previous';
                        let statusLabel = 'Tidigare';
                        let statusBg = 'secondary';
                        
                        if (validFrom > today) {
                          // Future assignment (starts after today)
                          status = 'upcoming';
                          statusLabel = 'Kommande';
                          statusBg = 'info';
                        } else if (validFrom <= today && (!validTo || validTo >= today)) {
                          // Current assignment (started on/before today and hasn't ended yet)
                          status = 'current';
                          statusLabel = 'Aktuell';
                          statusBg = 'success';
                        }
                        
                        const isCurrent = status === 'current';
                        
                        return (
                          <tr key={assignment.assign_id} className={isCurrent ? 'table-active' : ''}>
                            <td>
                              {formatDate(assignment.valid_from)}
                              {assignment.valid_to ? ` - ${formatDate(assignment.valid_to)}` : ' - Pågående'}
                            </td>
                            <td>
                              {assignment.position || '-'}
                              {assignment.position_level && (
                                <Badge bg="light" text="dark" className="ms-2 small">
                                  {assignment.position_level}
                                </Badge>
                              )}
                            </td>
                            <td>{assignment.department || '-'}</td>
                            <td>
                              {(assignment.manager_firstname || assignment.manager_lastname)
                                ? [assignment.manager_firstname, assignment.manager_lastname].filter(Boolean).join(' ')
                                : '-'}
                            </td>
                            <td>
                              <Badge bg={statusBg}>{statusLabel}</Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Collapse>
            </div>
          )}
        </div>
      </div>

      <UpdateAssignmentModal
        show={showUpdateModal}
        handleClose={() => setShowUpdateModal(false)}
        employeeId={employeeId}
        companyId={companyId}
        currentAssignment={modalMode === 'edit' ? currentAssignment : null}
        mode={modalMode}
        onSuccess={handleUpdateSuccess}
      />
    </>
  );
};

export default AssignmentCard;
